import { Box, Button, Container, Grid, Paper, Typography } from "@mui/material";
import {
  getDocs,
  collection,
  getFirestore,
  addDoc,
  onSnapshot,
} from "firebase/firestore";
import { map, orderBy } from "lodash";
import { useState, useEffect } from "react";
import felixAnalytics from "../../../felixAnalytics";
import { useUsuario } from "../../../providers/usuarioProvider";
import Credito from "../../../models/credito";
import CreditoDb from "../../../db/creditoDb";
import { IconArrowLeft, IconCoins, IconSparkles } from "@tabler/icons-react";
import InteiroText from "../../util/ui/inputs/inteiroText";
import { LoadingButton } from "@mui/lab";
import MoedaText from "../../util/ui/inputs/moedaText";
import { useNavigate } from "react-router-dom";
import { radius } from "../../../theme";

export default function Creditos() {
  const navigate = useNavigate();
  const { usuario } = useUsuario();
  const [comprando, setComprando] = useState(false);
  const [pacotes, setPacotes] = useState<
    { price: string; quantidade: number; preco: number }[]
  >([]);
  const [credito, setCredito] = useState<Credito>();

  useEffect(() => {
    if (!!usuario) {
      onSnapshot(
        new CreditoDb().doc({ creditoId: usuario.business || usuario.id }),
        (snapshot) => setCredito(snapshot.data()),
      );
    }
  }, [usuario?.business, usuario?.id]);

  useEffect(() => {
    getDocs(
      collection(getFirestore(), "/planos/prod_QnHXqQPDKHURyf/prices"),
    ).then((snap) =>
      setPacotes(
        map(snap.docs, (doc) => ({
          price: doc.ref.id,
          quantidade: doc.get("transform_quantity.divide_by"),
          preco: doc.get("unit_amount") / 100,
        })),
      ),
    );
  }, []);

  const handleComprar = async (price: string) => {
    if (!!!usuario) return;
    setComprando(true);
    felixAnalytics.track("comprar-creditos", { price });
    const docRef = await addDoc(
      collection(getFirestore(), `usuarios/${usuario.id}/checkout_sessions`),
      {
        price,
        quantity: 1,
        payment_method_types: ["card"],
        mode: "payment",
        billing_address_collection: "required",
        allow_promotion_codes: true,
        success_url: window.location.origin,
        cancel_url: window.location.href,
        customer_update: { name: "auto" },
        tax_id_collection: true,
      },
    );
    // Wait for the CheckoutSession to get attached by the extension
    onSnapshot(docRef, (snap) => {
      const { error, url }: any = snap.data();
      if (error) {
        setComprando(false);
        alert(`An error occured: ${error.message}`);
      }
      if (url) {
        setComprando(false);
        window.location.assign(url);
      }
    });
  };

  return (
    <Container
      maxWidth="md"
      sx={(theme) => ({
        mb: 2,
        padding: {
          md: theme.spacing(4, 8),
          xs: theme.spacing(2, 2),
        },
      })}
    >
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-start"
        marginBottom={2}
      >
        <Box flexGrow={1}>
          <Typography
            variant="eyebrow"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            Inteligência
          </Typography>
          <Typography variant="h1" color="text.primary">
            {`Créditos da IA da Felix`}
          </Typography>
        </Box>
        <Box m={1 / 2} />
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => navigate(-1)}
          startIcon={<IconArrowLeft />}
        >
          Voltar
        </Button>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          {/* Card de destaque: hairline + radius 2xl, sem preenchimento sólido
              (accent sólido reprovaria no contraste do texto por cima). */}
          <Paper
            elevation={0}
            sx={{
              bgcolor: "background.paper",
              border: 1,
              borderColor: "divider",
              borderRadius: `${radius.xxl}px`,
            }}
          >
            <Box
              py={2}
              px={4}
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography
                  variant="labelMono"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Saldo
                </Typography>
                <Typography
                  color="text.primary"
                  sx={{
                    fontVariantNumeric: "tabular-nums",
                    fontSize: 28,
                    fontWeight: 500,
                    lineHeight: 1.1,
                  }}
                >
                  F$ <InteiroText value={credito?.saldo || 0} />
                </Typography>
              </Box>
              <Box sx={{ color: "accentInk", opacity: 0.5, display: "flex" }}>
                <IconSparkles size={56} />
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}></Grid>
        {map(orderBy(pacotes, "quantidade", "asc"), (pacote) => (
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                height: "100%",
                bgcolor: "background.paper",
                border: 1,
                borderColor: "divider",
                borderRadius: `${radius.lg}px`,
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                height="100%"
                p={2}
                gap={1}
              >
                <Box py={2} sx={{ color: "accentInk", display: "flex" }}>
                  <IconCoins size={36} />
                </Box>
                <Typography
                  color="text.primary"
                  sx={{
                    fontVariantNumeric: "tabular-nums",
                    fontSize: 24,
                    fontWeight: 500,
                    lineHeight: 1,
                  }}
                >
                  <InteiroText value={pacote.quantidade} />
                </Typography>
                <Typography variant="labelMono" color="text.secondary">
                  Créditos
                </Typography>
                <Typography
                  mt={2}

                  color="text.primary"
                  sx={{
                    fontVariantNumeric: "tabular-nums",
                    fontSize: 18,
                    fontWeight: 500,
                  }}
                >
                  <MoedaText value={pacote.preco} />
                </Typography>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  fullWidth
                  loading={comprando}
                  onClick={() => handleComprar(pacote.price)}
                  sx={{ mt: "auto" }}
                >
                  Comprar
                </LoadingButton>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
